// packages/frontend/src/main.tsx
import * as React from 'react';
import * as ReactDOM from 'react-dom/client';
import { RouterProvider, createBrowserRouter } from 'react-router-dom';

import { ThemeProvider } from '@mui/material/styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import axios from 'axios';
import { getAuth } from 'firebase/auth';

import App from './App';
import ArbitratorDealsList from './components/ArbitratorDealsList';
import { AuthProvider } from './components/AuthContext';
import { BackgroundUploadsProvider } from './components/BackgroundUploads/BackgroundUploadsProvider';
import CGBillingClientList from './components/CGBillingClientList';
import CGNextPeriodInvoicesPreview from './components/CGBillingClientList/CGNextPeriodInvoicesPreview';
import CGPendingInvoicesList from './components/CGBillingClientList/CGPendingInvoicesList';
import CGPendingInvoicesPreview from './components/CGBillingClientList/CGPendingInvoicesPreview';
import InvoiceBatchesList from './components/CGBillingClientList/InvoiceBatchesList';
import CGFormRouter from './components/CGFormRouter';
import FormCGIAv2 from './components/FormCGIA/FormCGIAv2';
import CGReviewPage from './components/CGReview/CGReviewPage';
import ArbitragesCgiaPage from './components/ArbitragesCgia/ArbitragesCgiaPage';
import CGResubmissionPage from './components/CGResubmission/CGResubmissionPage';
import ThemeNextSandbox from './pages/ThemeNextSandbox';
import CGList from './components/CGList';
import CGMonthlyStats from './components/CGMonthlyStats';
import Cofinancement from './components/Cofinancement';
import CofinancementList from './components/CofinancementList';
import DAForm from './components/DAForm';
import DAMonthlyStats from './components/DAMonthlyStats';
import DAList from './components/DAList';
import FirebaseAccountList from './components/FirebaseAccountList';
import AgencyProfile from './components/Admin/AgencyProfile';
import MarketplaceDealsList from './components/MarketplaceDealsList';
import PrivateRoute from './components/PrivateRoute';
import SmartRedirect from './components/SmartRedirect';
import DetailedStatisticsPage from './components/Statistics/DetailedStatisticsPage';
import TestSmartRedirect from './components/TestSmartRedirect';
import './index.css';
import AuthPage from './pages/AuthPage';
import CarDealDetailsPage from './pages/CarDealDetailsPage';
import CompaniesListPage from './pages/CompaniesListPage';
import CreateCotation from './pages/CreateCotationPage';
import DealsByOrganizationPage from './pages/DealsByOrganizationPage';
import DealsListPage from './pages/DealsListPage';
import ErrorPage from './pages/ErrorPage';
import HomePage from './pages/HomePage';
import AgencyReprisePage from './pages/AgencyReprisePage';
import PasswordResetPage from './pages/PasswordResetPage';
import PromotionalBannerAdminPage from './pages/PromotionalBannerAdminPage';
import AgencyHomePromoMessageAdminPage from './pages/AgencyHomePromoMessageAdminPage';
import FeatureFlagsAdminPage from './pages/FeatureFlagsAdminPage';
import RFAAdminPage from './pages/RFAAdminPage';
import OrganizationsPersonsList from './components/Admin/OrganizationsPersonsList';
import OcrPage from './pages/OcrPage';
import LeadsVoCalendar from './pages/LeadsVo/Calendar';
import Onboarding from './pages/LeadsVo/Onboarding';
import LeadsVoPacks from './pages/LeadsVo/Packs';
import LeadsVoRdvs from './pages/LeadsVo/Rdvs';
import LeadsVoSettings from './pages/LeadsVo/Settings';
import AdminAgents from './pages/LeadsVo/admin/Agents';
import AdminAgentDetail from './pages/LeadsVo/admin/AgentDetail';
import AdminDashboard from './pages/LeadsVo/admin/Dashboard';
import AdminLeadsVoPacks from './pages/LeadsVo/admin/Packs';
import theme from './theme';

// Configurer l'intercepteur Axios
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    if (error?.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const auth = getAuth();
      const token = await auth.currentUser?.getIdToken(true);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      return axios(originalRequest);
    }
    return Promise.reject(error);
  },
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 500,
    },
  },
});

const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: '/',
        element: (
          <PrivateRoute>
            <HomePage />
          </PrivateRoute>
        ),
      },
      {
        path: '/login',
        element: <AuthPage />,
      },
      {
        path: '/password-reset',
        element: <PasswordResetPage />,
      },
      {
        path: 'deals-list',
        element: (
          <PrivateRoute>
            <DealsListPage key="deals-list" />
          </PrivateRoute>
        ),
      },
      {
        path: 'reprise',
        element: (
          <PrivateRoute>
            <AgencyReprisePage />
          </PrivateRoute>
        ),
      },
      {
        path: 'arbitrator',
        element: (
          <PrivateRoute>
            <ArbitratorDealsList key="arbitrator-list" />
          </PrivateRoute>
        ),
      },
      {
        path: 'accounts',
        element: (
          <PrivateRoute>
            <FirebaseAccountList />
          </PrivateRoute>
        ),
      },
      {
        path: 'deals-list/agency/:agencyId',
        element: (
          <PrivateRoute>
            <DealsListPage key="deals-list-agency" />
          </PrivateRoute>
        ),
      },
      {
        path: 'companies-list',
        element: (
          <PrivateRoute>
            <CompaniesListPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'deals-by-organization/:org_id',
        element: (
          <PrivateRoute>
            <DealsByOrganizationPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'car-deal/:id',
        element: <CarDealDetailsPage />,
      },
      {
        path: 'cotation',
        element: (
          <PrivateRoute allowExternalAccess={true}>
            <CreateCotation />
          </PrivateRoute>
        ),
      },
      {
        path: 'form-cg',
        element: (
          <PrivateRoute>
            <CGFormRouter />
          </PrivateRoute>
        ),
      },
      {
        path: 'form-cgia',
        element: (
          <PrivateRoute>
            <FormCGIAv2 />
          </PrivateRoute>
        ),
      },
      {
        path: 'theme-next-sandbox',
        element: (
          <PrivateRoute>
            <ThemeNextSandbox />
          </PrivateRoute>
        ),
      },
      {
        // Dashboard GOD review d'une CG créée via FormCGIA v2.
        // Le composant CGReviewPage gère lui-même la redirection si l'user
        // n'a pas le rôle `god`.
        path: 'cg/:registrationId/review',
        element: (
          <PrivateRoute>
            <CGReviewPage />
          </PrivateRoute>
        ),
      },
      {
        // Mode résumission agence : redéposer les pièces demandées par GOD
        // (cg_document_requests). Accessible quand cg_review_status est
        // awaiting_docs / awaiting_payment_and_docs.
        path: 'cg/:registrationId/resubmit',
        element: (
          <PrivateRoute>
            <CGResubmissionPage />
          </PrivateRoute>
        ),
      },
      {
        // Arbitrages CGIA : Skipcar tranche les décisions métier de l'analyse.
        // Réservé aux rôles internes, le backend refuse les autres.
        path: 'arbitrages-cgia',
        element: (
          <PrivateRoute>
            <ArbitragesCgiaPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'list-cg',
        element: (
          <PrivateRoute>
            <CGList />
          </PrivateRoute>
        ),
      },
      {
        path: 'cg-monthly-stats',
        element: (
          <PrivateRoute>
            <CGMonthlyStats />
          </PrivateRoute>
        ),
      },
      {
        path: 'da-monthly-stats',
        element: (
          <PrivateRoute>
            <DAMonthlyStats />
          </PrivateRoute>
        ),
      },
      {
        path: 'form-da',
        element: (
          <PrivateRoute>
            <DAForm />
          </PrivateRoute>
        ),
      },
      {
        path: 'list-da',
        element: (
          <PrivateRoute>
            <DAList />
          </PrivateRoute>
        ),
      },
      {
        path: 'billing-client-list',
        element: (
          <PrivateRoute>
            <CGBillingClientList />
          </PrivateRoute>
        ),
      },
      {
        path: 'pending-invoices',
        element: (
          <PrivateRoute>
            <CGPendingInvoicesList />
          </PrivateRoute>
        ),
      },
      {
        path: 'pending-invoices-preview',
        element: (
          <PrivateRoute>
            <CGPendingInvoicesPreview />
          </PrivateRoute>
        ),
      },
      {
        path: 'next-period-preview',
        element: (
          <PrivateRoute>
            <CGNextPeriodInvoicesPreview />
          </PrivateRoute>
        ),
      },
      {
        path: 'invoice-batches',
        element: (
          <PrivateRoute>
            <InvoiceBatchesList />
          </PrivateRoute>
        ),
      },
      {
        path: 'detailed-statistics',
        element: (
          <PrivateRoute>
            <DetailedStatisticsPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'marketplace-deals-list',
        element: (
          <PrivateRoute>
            <MarketplaceDealsList />
          </PrivateRoute>
        ),
      },
      {
        path: 'smart-redirect',
        element: <SmartRedirect />,
      },
      {
        path: 'test-smart-redirect',
        element: <TestSmartRedirect />,
      },
      {
        path: 'cofinancement/:dealId',
        element: (
          <PrivateRoute>
            <Cofinancement />
          </PrivateRoute>
        ),
      },
      {
        path: 'cofinancement-list',
        element: (
          <PrivateRoute>
            <CofinancementList />
          </PrivateRoute>
        ),
      },
      {
        path: 'admin/promotional-banner',
        element: (
          <PrivateRoute>
            <PromotionalBannerAdminPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'admin/agency-home-promo-message',
        element: (
          <PrivateRoute>
            <AgencyHomePromoMessageAdminPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'admin/feature-flags',
        element: (
          <PrivateRoute>
            <FeatureFlagsAdminPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'admin/rfa',
        element: (
          <PrivateRoute>
            <RFAAdminPage />
          </PrivateRoute>
        ),
      },
      {
        path: 'admin/agency/:orgId',
        element: (
          <PrivateRoute>
            <AgencyProfile />
          </PrivateRoute>
        ),
      },
      {
        path: 'admin/org-persons',
        element: (
          <PrivateRoute>
            <OrganizationsPersonsList />
          </PrivateRoute>
        ),
      },
      {
        path: 'ocr',
        element: (
          <PrivateRoute>
            <OcrPage />
          </PrivateRoute>
        ),
      },
      {
        path: '/leads-vo/onboarding',
        element: (
          <PrivateRoute>
            <Onboarding />
          </PrivateRoute>
        ),
      },
      {
        path: '/leads-vo/calendrier',
        element: (
          <PrivateRoute>
            <LeadsVoCalendar />
          </PrivateRoute>
        ),
      },
      {
        path: '/leads-vo/packs',
        element: (
          <PrivateRoute>
            <LeadsVoPacks />
          </PrivateRoute>
        ),
      },
      {
        path: '/leads-vo/rdvs',
        element: (
          <PrivateRoute>
            <LeadsVoRdvs />
          </PrivateRoute>
        ),
      },
      {
        path: '/leads-vo/settings',
        element: (
          <PrivateRoute>
            <LeadsVoSettings />
          </PrivateRoute>
        ),
      },
      {
        path: '/leads-vo/admin/agents',
        element: (
          <PrivateRoute>
            <AdminAgents />
          </PrivateRoute>
        ),
      },
      {
        path: '/leads-vo/admin/agents/:id',
        element: (
          <PrivateRoute>
            <AdminAgentDetail />
          </PrivateRoute>
        ),
      },
      {
        path: '/leads-vo/admin/dashboard',
        element: (
          <PrivateRoute>
            <AdminDashboard />
          </PrivateRoute>
        ),
      },
      {
        path: '/leads-vo/admin/packs',
        element: (
          <PrivateRoute>
            <AdminLeadsVoPacks />
          </PrivateRoute>
        ),
      },
    ],
  },
]);

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <AuthProvider>
          <BackgroundUploadsProvider>
            <RouterProvider router={router} />
          </BackgroundUploadsProvider>
        </AuthProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
